import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';

import { Observable, of, throwError, EMPTY } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { SecurityErrorComponent } from 'src/app/shared/components/security-error/security-error.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SpinnerLoaderService } from '../services/spinner-loader/spinner-loader.service';
import { SKIP_ERROR_HANDLER } from '../http-context-token';
import { BUTTON_LOADING_ID } from 'src/app/shared/global/http-context-tokens';
import { UserService } from '../services/user/user.service';
import { extractErrorMessage } from '../services/uitls/error.util';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  
  securityErrorDialogRef!: MatDialogRef<SecurityErrorComponent>;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    private spinnerService: SpinnerLoaderService,
    private route: ActivatedRoute,
    private userService: UserService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const userData = this.userService.getCurrentUser();
    const buttonId = request.context.get(BUTTON_LOADING_ID);
    request = request.clone({
      headers: request.headers.set('Accept-Language', 'ar'),
    });

    if (buttonId) {
      this.spinnerService.setLoading(buttonId, true);
    }

    if (this.userService.isAuthenticated()) {
      const token = this.userService.accessToken;
      if (token) {
        request = this.addTokenHeader(request, token);
      }
      if (request.body instanceof FormData) {
        if (!request.headers.has('Content-Type')) {
          request = request.clone({
            headers: request.headers.set['Content-Type'],
          });
        }

        request = request.clone({
          headers: request.headers.set['Accept'],
        });
      } else {
        if (!request.headers.has('Content-Type')) {
          request = request.clone({
            headers: request.headers.set('Content-Type', 'application/json'),
          });
        }

        request = request.clone({
          headers: request.headers.set('Accept', 'application/json'),
        });
      }
    }

    if (request.context.get(SKIP_ERROR_HANDLER)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        return event;
      }),
      finalize(() => {
        this.spinnerService.setLoading(buttonId, false);
      }),
      catchError((error: HttpErrorResponse) => {
        // Add context to the error for better debugging
        const errorContext = {
          url: request.url,
          method: request.method,
          status: error.status,
          statusText: error.statusText,
          message: error.message,
        };
        console.error('HTTP Error:', errorContext);
        return this.handleAuthError(error, request, userData, next, buttonId);
      })
    );
  }

  private handleAuthError(
    err: HttpErrorResponse,
    request,
    userData,
    next,
    buttonId
  ): Observable<any> {
    // Handle network errors (status 0 means network failure or CORS issue)
    if (err.status === 0) {
      console.error('Network error or CORS issue:', {
        url: request.url,
        error: err.message,
      });
      // Return a user-friendly error instead of throwing
      return throwError(() => ({
        status: 0,
        message: 'فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.',
        error: err,
      }));
    }

    if (!request.url.includes('auth/refresh') && err.status == 401) {
      // Check if userData exists and has refresh_token before attempting refresh
      if (!userData || !userData.refresh_token) {
        return this.relogin(err);
      }
      const refreshToken = userData.refresh_token;
      return this.authenticationService.refreshToken(refreshToken).pipe(
        switchMap((res) => {
          request = this.addTokenHeader(request, res.access);
          this.userService.updateAccessToken(res.access);
          this.authenticationService.setLoggedInUser(null);
          return next.handle(request).pipe(
            map((event: HttpEvent<any>) => {
              return event;
            }),
            catchError(() => {
              return this.relogin(err);
            })
          );
        }),
        catchError(() => {
          return this.relogin(err);
        })
      );
    } else if (err.status === 429 || err.status == 403) {
      if (this.securityErrorDialogRef == null) {
        const errorMsg = extractErrorMessage(err, 'غير مصرح لك بالوصول');
        this.securityErrorDialogRef = this.dialog.open(SecurityErrorComponent, {
          data: {
            message: errorMsg,
          },
        });
        this.securityErrorDialogRef.afterClosed().subscribe((result) => {
          this.securityErrorDialogRef = null;
        });
      }
      this.spinnerService.setLoading(buttonId, false);
      return EMPTY;
    } else if (err.status === 400) {
      // Log validation errors for debugging
      console.error('Bad Request (400):', {
        url: request.url,
        error: err.error,
        message: err.message,
      });
      return throwError(err);
    } else if (err.status == 404) {
      return throwError(err);      
    } else if (err.status == 500) {
      this.router.navigate(['/pages/ServerError']);
    } else {
      return throwError(err);
    }
  }

  relogin(err) {
    this.userService.clearAll();
    this.dialog.closeAll();
    let queryParams = { ...this.route.snapshot.queryParams }; 
    if (!queryParams['returnUrl']) {
      queryParams['returnUrl'] = this.router.routerState?.snapshot?.url || '/';
    }

    this.router.navigate(['/auth/login'], {
      queryParams,
      queryParamsHandling: 'merge',
    });
    return throwError(err.message);
  }

  addTokenHeader(request, token) {
    request = request.clone({
      headers: request.headers.set('Authorization', `JWT ${token}`),
    });
    return request;
  }
}
